import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faChevronLeft,
  faChevronRight,
  faStar as faStarSolid,
  faEdit,
  faUser,
  faImage,
  faFile,
  faThLarge,
  faTable,
  faDownload,
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp, faGoogle } from '@fortawesome/free-brands-svg-icons';
import { useGoogleLogin } from '@react-oauth/google';
import ExcelJS from 'exceljs';
import toast from 'react-hot-toast';

import { api } from '@/lib/axios';
import { API_ROUTES, ROUTES, getAttributeTranslation, DEFAULT_CITY } from '@/constants';
import { Button, Card, ImageLightbox, Spinner } from '@/components/ui';
import { Input, Select } from '@/components/forms';
import { Pill } from '@/components/ui/Pill/Pill';
import { useAuthStore } from '@/stores';
import type { ProviderContact } from '@/types/provider';
import { ProviderUsersSection } from './ProviderUsers';
import styles from './ProviderDetails.module.css';

interface Service {
  id: string;
  name: string;
  category_id?: string;
}

interface Attribute {
  id: string;
  name: string;
}

interface PricingUnit {
  id: string;
  name: string;
}

interface CostItem {
  price: number | string;
  currency: string;
  description?: string;
  pricing_unit_details: PricingUnit;
}

interface SpecItem {
  attribute_details: Attribute;
  value: string;
}

interface Category {
  id: string;
  nombre: string;
}

interface Offering {
  id: string;
  service_details: Service;
  description: string;
  specifications: SpecItem[];
  costs: CostItem[];
}

function buildOfferingsCsv(offerings: Offering[], providerName: string): void {
  const rows: string[][] = [
    ['Service', 'Description', 'Price', 'Unit', 'Currency', 'Notes', 'Attributes'],
  ];

  for (const offering of offerings) {
    const specs = (offering.specifications || [])
      .map((s) => `${getAttributeTranslation(s.attribute_details?.name || '')}: ${s.value}`)
      .join('; ');
    const costs = offering.costs || [];

    if (costs.length === 0) {
      rows.push([
        offering.service_details.name,
        offering.description || '',
        '',
        '',
        '',
        '',
        specs,
      ]);
    } else {
      for (let i = 0; i < costs.length; i++) {
        const cost = costs[i];
        const price = typeof cost.price === 'string' ? parseFloat(cost.price) : cost.price;
        rows.push([
          i === 0 ? offering.service_details.name : '',
          i === 0 ? (offering.description || '') : '',
          isNaN(price) ? '' : price.toFixed(2),
          cost.pricing_unit_details?.name || '',
          cost.currency || '',
          cost.description || '',
          i === 0 ? specs : '',
        ]);
      }
    }
  }

  const csv = rows.map((r) => r.map((v) => `"${v.replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${providerName.replace(/[^a-zA-Z0-9]/g, '_')}_services.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

async function buildOfferingsXlsx(offerings: Offering[]): Promise<Uint8Array> {
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet('Services');

  ws.columns = [
    { header: 'Service', key: 'service', width: 24 },
    { header: 'Description', key: 'description', width: 32 },
    { header: 'Price', key: 'price', width: 14 },
    { header: 'Unit', key: 'unit', width: 16 },
    { header: 'Currency', key: 'currency', width: 10 },
    { header: 'Notes', key: 'cost_note', width: 28 },
    { header: 'Attributes', key: 'attributes', width: 40 },
  ];

  const headerRow = ws.getRow(1);
  headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
  headerRow.fill = {
    type: 'pattern' as const,
    pattern: 'solid' as const,
    fgColor: { argb: 'FF0C1B33' },
  };

  for (const offering of offerings) {
    const specs = (offering.specifications || [])
      .map((s) => `${getAttributeTranslation(s.attribute_details?.name || '')}: ${s.value}`)
      .join('; ');
    const costs = offering.costs || [];

    if (costs.length === 0) {
      ws.addRow({
        service: offering.service_details.name,
        description: offering.description || '',
        price: '',
        unit: '',
        currency: '',
        cost_note: '',
        attributes: specs,
      });
    } else {
      for (let i = 0; i < costs.length; i++) {
        const cost = costs[i];
        const price = typeof cost.price === 'string' ? parseFloat(cost.price) : cost.price;
        ws.addRow({
          service: i === 0 ? offering.service_details.name : '',
          description: i === 0 ? (offering.description || '') : '',
          price: isNaN(price) ? '' : price,
          unit: cost.pricing_unit_details?.name || '',
          currency: cost.currency || '',
          cost_note: cost.description || '',
          attributes: i === 0 ? specs : '',
        });
      }
    }
  }

  ws.getColumn('price').numFmt = '#,##0.00';

  const buffer = await wb.xlsx.writeBuffer();
  return new Uint8Array(buffer as ArrayBuffer);
}

async function uploadOfferingsToSheets(
  offerings: Offering[],
  providerName: string,
  accessToken: string,
): Promise<string> {
  const xlsxBytes = await buildOfferingsXlsx(offerings);
  const fileName = `${providerName} - Services`;

  const metadata = {
    name: fileName,
    mimeType: 'application/vnd.google-apps.spreadsheet',
  };

  const boundary = 'ebombo_provider_boundary';
  const body = new Uint8Array(
    await new Blob([
      `--${boundary}\r\nContent-Type: application/json; charset=UTF-8\r\n\r\n`,
      JSON.stringify(metadata),
      `\r\n--${boundary}\r\nContent-Type: application/vnd.openxmlformats-officedocument.spreadsheetml.sheet\r\n\r\n`,
      xlsxBytes,
      `\r\n--${boundary}--\r\n`,
    ]).arrayBuffer(),
  );

  const resp = await fetch(
    'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart',
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': `multipart/related; boundary=${boundary}`,
      },
      body,
    },
  );

  if (!resp.ok) {
    const err = await resp.text();
    throw new Error(`Drive upload failed: ${err}`);
  }

  const fileData = (await resp.json()) as { id: string };
  return `https://docs.google.com/spreadsheets/d/${fileData.id}/edit`;
}

export function ProviderDetails() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { isStaffOrAbove } = useAuthStore();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('table');
  const [expandedOfferingId, setExpandedOfferingId] = useState<string | null>(null);
  const [expandedSearchQuery, setExpandedSearchQuery] = useState('');
  const [showCreatorPopover, setShowCreatorPopover] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [isSheetsExporting, setIsSheetsExporting] = useState(false);
  const [sheetsUrl, setSheetsUrl] = useState<string | null>(null);
  const creatorRef = useRef<HTMLDivElement>(null);
  const expandedPaneRef = useRef<HTMLDivElement>(null);

  // Close popover on outside click
  useEffect(() => {
    if (!showCreatorPopover) return;
    const handler = (e: MouseEvent) => {
      if (creatorRef.current && !creatorRef.current.contains(e.target as Node)) {
        setShowCreatorPopover(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showCreatorPopover]);

  // Fetch provider
  const { data: provider, isLoading: isLoadingProvider } = useQuery({
    queryKey: ['provider', id],
    queryFn: async () => {
      const response = await api.get(`${API_ROUTES.providers}${id}/`);
      return response.data;
    },
    enabled: !!id,
  });

  // Fetch offerings
  const { data: offeringsData, isLoading: isLoadingOfferings } = useQuery({
    queryKey: ['offerings', id],
    queryFn: async () => {
      const response = await api.get(`${API_ROUTES.offerings}?provider=${id}`);
      return response.data.results || [];
    },
    enabled: !!id,
  });

  // Fetch categories for filter
  const { data: categories } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const response = await api.get(API_ROUTES.categories);
      return Array.isArray(response.data) ? response.data : response.data.results || [];
    },
    staleTime: 5 * 60 * 1000,
  });

  const offerings = useMemo(() => offeringsData || [], [offeringsData]);

  // Fetch all provider IDs for prev/next navigation
  const { data: providerIds } = useQuery<string[]>({
    queryKey: ['provider-ids'],
    queryFn: async () => {
      const response = await api.get(`${API_ROUTES.providers}?limit=500&fields=id`);
      const results = response.data.results || response.data || [];
      return results.map((p: { id: string }) => p.id);
    },
    staleTime: 60_000,
  });

  const currentIndex = providerIds?.indexOf(id ?? '') ?? -1;
  const prevId = currentIndex > 0 ? providerIds![currentIndex - 1] : null;
  const nextId =
    providerIds && currentIndex < providerIds.length - 1 ? providerIds[currentIndex + 1] : null;

  // Filter offerings
  const filteredOfferings = useMemo(() => {
    return offerings.filter((offering: Offering) => {
      const matchesSearch =
        offering.service_details.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (offering.description || '').toLowerCase().includes(searchQuery.toLowerCase());

      const matchesCategory = selectedCategory
        ? String(offering.service_details.category_id) === selectedCategory
        : true;

      return matchesSearch && matchesCategory;
    });
  }, [offerings, searchQuery, selectedCategory]);

  const googleLoginForSheets = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setIsSheetsExporting(true);
      toast.loading('Exporting to Google Sheets...', { id: 'provider-sheets' });
      try {
        const sheetUrl = await uploadOfferingsToSheets(
          filteredOfferings,
          provider?.nombre_comercial || 'Provider',
          tokenResponse.access_token,
        );
        setSheetsUrl(sheetUrl);
        toast.success('Done! Click "Open" to view your sheet.', {
          id: 'provider-sheets',
          duration: 8000,
        });
      } catch {
        toast.error('Error exporting to Google Sheets', { id: 'provider-sheets' });
      } finally {
        setIsSheetsExporting(false);
      }
    },
    onError: () => {
      toast.error('Google sign-in cancelled');
    },
    scope: 'https://www.googleapis.com/auth/drive.file',
  });

  const handleDownloadCsv = useCallback(() => {
    buildOfferingsCsv(filteredOfferings, provider?.nombre_comercial || 'Provider');
  }, [filteredOfferings, provider?.nombre_comercial]);

  const handleEditProvider = () => {
    navigate(`/dashboard/providers/${id}/edit`);
  };

  const handleWhatsApp = () => {
    if (!provider?.telefono) return;
    const cleanPhone = provider.telefono.replace(/\D/g, '');
    if (cleanPhone.length < 7) return;
    window.open(`https://wa.me/${cleanPhone}`, '_blank');
  };

  const isValidPhone = (phone: string | undefined) => {
    if (!phone) return false;
    const clean = phone.replace(/\D/g, '');
    return clean.length >= 7;
  };

  const toggleOffering = (offeringId: string) => {
    if (expandedOfferingId === offeringId) {
      setExpandedOfferingId(null);
      setExpandedSearchQuery('');
    } else {
      setExpandedOfferingId(offeringId);
      setExpandedSearchQuery('');
      setTimeout(
        () => expandedPaneRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
        100,
      );
    }
  };

  const parsePrice = (price: number | string) => {
    const parsed = typeof price === 'string' ? parseFloat(price) : price;
    return isNaN(parsed) ? 0 : parsed;
  };

  const expandedOffering = useMemo(
    () => offerings.find((o: Offering) => o.id === expandedOfferingId),
    [offerings, expandedOfferingId],
  );

  const filteredExpandedCosts = useMemo(() => {
    if (!expandedOffering) return [];
    if (!expandedSearchQuery) return expandedOffering.costs || [];
    const q = expandedSearchQuery.toLowerCase();
    return (expandedOffering.costs || []).filter(
      (cost: CostItem) =>
        cost.currency?.toLowerCase().includes(q) ||
        cost.price?.toString().includes(q) ||
        cost.pricing_unit_details?.name?.toLowerCase().includes(q) ||
        cost.description?.toLowerCase().includes(q),
    );
  }, [expandedOffering, expandedSearchQuery]);

  const filteredExpandedSpecs = useMemo(() => {
    if (!expandedOffering) return [];
    if (!expandedSearchQuery) return expandedOffering.specifications || [];
    const q = expandedSearchQuery.toLowerCase();
    return (expandedOffering.specifications || []).filter(
      (spec: SpecItem) =>
        getAttributeTranslation(spec.attribute_details?.name || '')
          .toLowerCase()
          .includes(q) || spec.value?.toLowerCase().includes(q),
    );
  }, [expandedOffering, expandedSearchQuery]);

  if (isLoadingProvider || isLoadingOfferings) {
    return (
      <div className={styles.container}>
        <Spinner />
      </div>
    );
  }

  if (!provider) {
    return (
      <div className={styles.container}>
        <Card>
          <div className={styles.emptyState}>Provider not found.</div>
          <Button onClick={() => navigate(ROUTES.dashboardProviders)}>Back to List</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.titleGroup}>
          <Button variant="secondary" onClick={() => navigate(ROUTES.dashboardProviders)}>
            <FontAwesomeIcon icon={faArrowLeft} />
          </Button>
          <h1 className={styles.title}>{provider.nombre_comercial}</h1>
          <span
            className={`${styles.statusBadge} ${
              provider.state === 'activo' ? styles.statusActive : styles.statusInactive
            }`}
          >
            {provider.state === 'activo' ? 'Active' : 'Inactive'}
          </span>
        </div>
        <div className={styles.headerActions}>
          <div className={styles.navButtons}>
            <Button
              variant="secondary"
              size="sm"
              disabled={!prevId}
              onClick={() =>
                prevId && navigate(ROUTES.dashboardProviderDetails.replace(':id', String(prevId)))
              }
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={!nextId}
              onClick={() =>
                nextId && navigate(ROUTES.dashboardProviderDetails.replace(':id', String(nextId)))
              }
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </Button>
          </div>
          <Button variant="primary" onClick={handleEditProvider}>
            <FontAwesomeIcon icon={faEdit} /> Edit Provider
          </Button>
        </div>
      </div>

      <div className={styles.contentWrapper}>
        {/* Basic Info Summary */}
        <Card className={styles.stepContent}>
          <div className={styles.reviewSection}>
            <h3>Basic Info</h3>
            <div className={styles.reviewItem}>
              <label>Provider:</label>
              <span>{provider.nombre_comercial}</span>
            </div>
            <div className={styles.reviewItem}>
              <label>Location:</label>
              <span>{[provider.distrito, provider.ciudad, provider.pais].filter(Boolean).join(', ') || DEFAULT_CITY}</span>
            </div>
            {provider.calificacion > 0 && (
              <div className={styles.reviewItem}>
                <label>Rating:</label>
                <span className={styles.stars}>
                  {[...Array(provider.calificacion)].map((_, i) => (
                    <FontAwesomeIcon key={i} icon={faStarSolid} className={styles.starFilled} />
                  ))}
                </span>
              </div>
            )}
            <div className={styles.reviewItem}>
              <label>Services Added:</label>
              <span>{offerings.length} service(s)</span>
            </div>
            {provider.contacts?.length > 0 ? (
              provider.contacts.map((contact: ProviderContact, idx: number) => (
                <div key={contact.id || idx} className={styles.contactBlock}>
                  {contact.nombre && (
                    <div className={styles.reviewItem}>
                      <label>{contact.es_principal ? 'Primary Contact:' : 'Contact:'}</label>
                      <span>{contact.nombre}{contact.cargo ? ` (${contact.cargo})` : ''}</span>
                    </div>
                  )}
                  {contact.email && (
                    <div className={styles.reviewItem}>
                      <label>Email:</label>
                      <span>{contact.email}</span>
                    </div>
                  )}
                  <div className={styles.reviewItem}>
                    <label>Phone:</label>
                    <span className={styles.phoneValue}>
                      {contact.telefono || '-'}
                      {isValidPhone(contact.telefono ?? undefined) && (
                        <Button
                          variant="secondary"
                          size="sm"
                          className={styles.buttonWhatsapp}
                          onClick={() => {
                            const cleanPhone = (contact.telefono || '').replace(/\D/g, '');
                            if (cleanPhone.length >= 7) window.open(`https://wa.me/${cleanPhone}`, '_blank');
                          }}
                          title="Contact via WhatsApp"
                        >
                          <FontAwesomeIcon icon={faWhatsapp} />
                        </Button>
                      )}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <>
                {provider.email && (
                  <div className={styles.reviewItem}>
                    <label>Email:</label>
                    <span>{provider.email}</span>
                  </div>
                )}
                <div className={styles.reviewItem}>
                  <label>Phone:</label>
                  <span className={styles.phoneValue}>
                    {provider.telefono || '-'}
                    {isValidPhone(provider.telefono) && (
                      <Button
                        variant="secondary"
                        size="sm"
                        className={styles.buttonWhatsapp}
                        onClick={handleWhatsApp}
                        title="Contact via WhatsApp"
                      >
                        <FontAwesomeIcon icon={faWhatsapp} />
                      </Button>
                    )}
                  </span>
                </div>
              </>
            )}
          </div>
        </Card>

        {/* Images Gallery */}
        {provider.images?.length > 0 && (
          <Card className={styles.stepContent}>
            <div className={styles.reviewSection}>
              <h3>
                <FontAwesomeIcon icon={faImage} style={{ marginRight: 8, opacity: 0.6 }} />
                Images ({provider.images.length})
              </h3>
              <div className={styles.imageGallery}>
                {provider.images.map(
                  (img: { url: string; name: string; source_document?: string }, i: number) => (
                    <button
                      key={i}
                      type="button"
                      className={styles.galleryItem}
                      title={img.name}
                      onClick={() => setLightboxIndex(i)}
                      aria-label={`Open image ${i + 1}`}
                    >
                      <img
                        src={img.url}
                        alt={img.name}
                        className={styles.galleryThumb}
                        loading="lazy"
                      />
                      {img.source_document && (
                        <span className={styles.gallerySource}>{img.source_document}</span>
                      )}
                    </button>
                  ),
                )}
              </div>
            </div>
          </Card>
        )}

        <ImageLightbox
          images={provider.images?.map((img: { url: string }) => img.url) ?? []}
          openIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          downloadFilename={`${provider.nombre_comercial || 'provider'}-image`}
        />

        {/* Documents */}
        {provider.documents?.length > 0 && (
          <Card className={styles.stepContent}>
            <div className={styles.reviewSection}>
              <h3>
                <FontAwesomeIcon icon={faFile} style={{ marginRight: 8, opacity: 0.6 }} />
                Documents ({provider.documents.length})
              </h3>
              <div className={styles.documentList}>
                {provider.documents.map((doc: { url: string; name: string }, i: number) => (
                  <a
                    key={i}
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.documentItem}
                  >
                    <FontAwesomeIcon icon={faFile} className={styles.documentIcon} />
                    <span>{doc.name}</span>
                  </a>
                ))}
              </div>
            </div>
          </Card>
        )}

        {/* Creator badge */}
        <div className={styles.creatorRow} ref={creatorRef}>
          <button
            className={styles.creatorBadge}
            onClick={() =>
              (provider.temp_creator || provider.created_by_email) &&
              setShowCreatorPopover((v) => !v)
            }
            style={{
              cursor: provider.temp_creator || provider.created_by_email ? 'pointer' : 'default',
            }}
          >
            <FontAwesomeIcon icon={faUser} />
            <span>
              Created by: {provider.created_by_email || provider.temp_creator?.name || '—'}
            </span>
          </button>
          {showCreatorPopover && (provider.created_by_email || provider.temp_creator) && (
            <div className={styles.creatorPopover}>
              {provider.created_by_email && (
                <div className={styles.creatorPopoverRow}>
                  <span className={styles.creatorPopoverLabel}>User</span>
                  <span>{provider.created_by_email}</span>
                </div>
              )}
              {provider.temp_creator && (
                <>
                  <div className={styles.creatorPopoverRow}>
                    <span className={styles.creatorPopoverLabel}>Name</span>
                    <span>{provider.temp_creator.name}</span>
                  </div>
                  <div className={styles.creatorPopoverRow}>
                    <span className={styles.creatorPopoverLabel}>Email</span>
                    <span>{provider.temp_creator.email}</span>
                  </div>
                  {provider.temp_creator.country && (
                    <div className={styles.creatorPopoverRow}>
                      <span className={styles.creatorPopoverLabel}>Country</span>
                      <span>{provider.temp_creator.country}</span>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Offerings Section */}
        <Card className={styles.stepContent}>
          <div className={styles.servicesHeader}>
            <h3>Services / Offerings</h3>

            <div className={styles.filterGroup}>
              <Input
                name="searchServices"
                placeholder="Search services..."
                value={searchQuery}
                setValue={setSearchQuery}
                variant="bordered"
                isClearable
              />
              <Select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                options={[
                  { value: '', label: 'All Categories' },
                  ...(Array.isArray(categories)
                    ? categories.map((c: Category) => ({
                        value: c.id.toString(),
                        label: c.nombre,
                      }))
                    : []),
                ]}
                size="md"
              />
              <div className={styles.viewToggle}>
                <button
                  className={`${styles.viewToggleBtn} ${viewMode === 'cards' ? styles.viewToggleBtnActive : ''}`}
                  onClick={() => setViewMode('cards')}
                  title="Cards view"
                  aria-label="Cards view"
                >
                  <FontAwesomeIcon icon={faThLarge} />
                </button>
                <button
                  className={`${styles.viewToggleBtn} ${viewMode === 'table' ? styles.viewToggleBtnActive : ''}`}
                  onClick={() => setViewMode('table')}
                  title="Table view"
                  aria-label="Table view"
                >
                  <FontAwesomeIcon icon={faTable} />
                </button>
              </div>
            </div>
          </div>

          {viewMode === 'cards' ? (
            <>
              <div className={styles.servicesList}>
                {filteredOfferings.length === 0 ? (
                  <div className={styles.emptyState}>
                    <p>No matching services found.</p>
                  </div>
                ) : (
                  filteredOfferings.map((offering: Offering) => (
                    <div
                      key={offering.id}
                      className={`${styles.serviceCard} ${expandedOfferingId === offering.id ? styles.serviceCardExpanded : ''}`}
                      onClick={() => toggleOffering(offering.id)}
                    >
                      <div className={styles.serviceHeader}>
                        <h4>{offering.service_details.name}</h4>
                      </div>
                      {offering.description && (
                        <p className={styles.serviceDescription}>{offering.description}</p>
                      )}
                      <div className={styles.pillsContainer}>
                        {(() => {
                          const allPills = [
                            ...(offering.costs || []).map((cost, i) => (
                              <Pill key={`cost-${i}`} variant="info">
                                {cost.currency} {cost.price} / {cost.pricing_unit_details?.name}
                              </Pill>
                            )),
                            ...(offering.specifications || []).map((spec, i) => (
                              <Pill key={`spec-${i}`} variant="secondary">
                                {getAttributeTranslation(spec.attribute_details?.name || '')}:{' '}
                                {spec.value}
                              </Pill>
                            )),
                          ];
                          const MAX_PILLS = 6;
                          const visiblePills = allPills.slice(0, MAX_PILLS);
                          const remaining = allPills.length - MAX_PILLS;

                          return (
                            <>
                              {visiblePills}
                              {remaining > 0 && <Pill variant="secondary">+{remaining} more</Pill>}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Expanded Inline Details below the grid */}
              {expandedOffering && (
                <div ref={expandedPaneRef} className={styles.expandedContentPane}>
                  <div className={styles.paneHeader}>
                    <h4>Details: {expandedOffering.service_details.name}</h4>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <Button
                        variant="primary"
                        onClick={() => {
                          navigate(`/dashboard/providers/${id}/edit?offering=${expandedOffering.id}`);
                        }}
                        size="sm"
                      >
                        <FontAwesomeIcon icon={faEdit} /> Edit
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={() => {
                          setExpandedOfferingId(null);
                          setExpandedSearchQuery('');
                        }}
                        size="sm"
                      >
                        Close
                      </Button>
                    </div>
                  </div>

                  <div className={styles.paneBody}>
                    <div style={{ marginBottom: '1rem', maxWidth: '400px' }}>
                      <Input
                        name="searchExpanded"
                        placeholder="Search prices or attributes..."
                        value={expandedSearchQuery}
                        setValue={setExpandedSearchQuery}
                        variant="bordered"
                        isClearable
                      />
                    </div>

                    <div className={styles.detailSection}>
                      <div className={styles.detailSectionHeader}>
                        <h5>
                          Pricing & Costs{' '}
                          {expandedOffering.costs?.length > 0 && (
                            <span
                              style={{
                                fontSize: '0.8rem',
                                marginLeft: '8px',
                                color: 'var(--color-text-secondary)',
                              }}
                            >
                              ({filteredExpandedCosts.length}/{expandedOffering.costs.length})
                            </span>
                          )}
                        </h5>
                      </div>
                      {filteredExpandedCosts.length > 0 ? (
                        <div className={styles.costList}>
                          {filteredExpandedCosts.map((cost: CostItem, idx: number) => (
                            <div key={`cost-${idx}`} className={styles.costItem}>
                              <div className={styles.costMain}>
                                <span className={styles.costPrice}>
                                  {cost.currency} {parsePrice(cost.price).toFixed(2)}
                                </span>
                                <span className={styles.costUnit}>
                                  / {cost.pricing_unit_details?.name}
                                </span>
                              </div>
                              {cost.description && (
                                <div className={styles.costDesc}>{cost.description}</div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className={styles.emptyText}>
                          No pricing information available matching your search.
                        </p>
                      )}
                    </div>

                    {expandedOffering.specifications?.length > 0 && (
                      <div className={styles.detailSection}>
                        <div className={styles.detailSectionHeader}>
                          <h5>
                            Specifications{' '}
                            <span
                              style={{
                                fontSize: '0.8rem',
                                marginLeft: '8px',
                                color: 'var(--color-text-secondary)',
                              }}
                            >
                              ({filteredExpandedSpecs.length}/{expandedOffering.specifications.length})
                            </span>
                          </h5>
                        </div>
                        {filteredExpandedSpecs.length > 0 ? (
                          <div className={styles.specGrid}>
                            {filteredExpandedSpecs.map((spec: SpecItem, idx: number) => (
                              <div key={`spec-${idx}`} className={styles.specItem}>
                                <span className={styles.specLabel}>
                                  {getAttributeTranslation(spec.attribute_details?.name || '')}
                                </span>
                                <span className={styles.specValue}>{spec.value}</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className={styles.emptyText}>
                            No specifications available matching your search.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className={styles.offeringsTableWrapper}>
              <div className={styles.tableActions}>
                <button
                  className={styles.tableActionBtn}
                  onClick={handleDownloadCsv}
                  disabled={filteredOfferings.length === 0}
                  title="Download CSV"
                >
                  <FontAwesomeIcon icon={faDownload} />
                  <span>CSV</span>
                </button>
                {sheetsUrl ? (
                  <a
                    href={sheetsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`${styles.tableActionBtn} ${styles.sheetsBtnReady}`}
                    title="Open in Google Sheets"
                  >
                    <FontAwesomeIcon icon={faGoogle} />
                    <span>Open</span>
                  </a>
                ) : (
                  <button
                    className={`${styles.tableActionBtn} ${styles.sheetsBtn}`}
                    onClick={() => googleLoginForSheets()}
                    disabled={isSheetsExporting || filteredOfferings.length === 0}
                    title="Export to Google Sheets"
                  >
                    <FontAwesomeIcon icon={faGoogle} />
                    <span>{isSheetsExporting ? 'Exporting...' : 'Sheets'}</span>
                  </button>
                )}
              </div>
              {filteredOfferings.length === 0 ? (
                <div className={styles.emptyState}>
                  <p>No matching services found.</p>
                </div>
              ) : (
                <table className={styles.offeringsTable}>
                  <thead>
                    <tr>
                      <th>Service</th>
                      <th>Description</th>
                      <th className={styles.numCol}>Price</th>
                      <th>Unit</th>
                      <th>Currency</th>
                      <th>Notes</th>
                      <th>Attributes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOfferings.map((offering: Offering) => {
                      const costs = offering.costs || [];
                      const specs = offering.specifications || [];
                      const rowCount = Math.max(costs.length, 1);

                      return costs.length === 0 ? (
                        <tr key={offering.id} className={styles.tableEvenRow}>
                          <td className={styles.tableServiceCell}>
                            {offering.service_details.name}
                          </td>
                          <td className={styles.tableDescCell}>
                            {offering.description || <span className={styles.tableDash}>—</span>}
                          </td>
                          <td className={`${styles.numCol} ${styles.tableDash}`}>—</td>
                          <td className={styles.tableDash}>—</td>
                          <td className={styles.tableDash}>—</td>
                          <td className={styles.tableDash}>—</td>
                          <td>
                            {specs.length > 0 ? (
                              <div className={styles.tableSpecsList}>
                                {specs.map((spec, si) => (
                                  <span key={si} className={styles.tableSpecTag}>
                                    {getAttributeTranslation(spec.attribute_details?.name || '')}:{' '}
                                    {spec.value}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className={styles.tableDash}>—</span>
                            )}
                          </td>
                        </tr>
                      ) : (
                        costs.map((cost: CostItem, ci: number) => (
                          <tr
                            key={`${offering.id}-${ci}`}
                            className={ci % 2 === 0 ? styles.tableEvenRow : styles.tableOddRow}
                          >
                            {ci === 0 && (
                              <>
                                <td
                                  className={styles.tableServiceCell}
                                  rowSpan={rowCount}
                                >
                                  {offering.service_details.name}
                                </td>
                                <td
                                  className={styles.tableDescCell}
                                  rowSpan={rowCount}
                                >
                                  {offering.description || <span className={styles.tableDash}>—</span>}
                                </td>
                              </>
                            )}
                            <td className={`${styles.numCol} ${styles.tablePriceCell}`}>
                              {parsePrice(cost.price).toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </td>
                            <td>{cost.pricing_unit_details?.name || '—'}</td>
                            <td>{cost.currency}</td>
                            <td className={styles.tableCostNoteCell}>
                              {cost.description || <span className={styles.tableDash}>—</span>}
                            </td>
                            {ci === 0 && (
                              <td rowSpan={rowCount}>
                                {specs.length > 0 ? (
                                  <div className={styles.tableSpecsList}>
                                    {specs.map((spec, si) => (
                                      <span key={si} className={styles.tableSpecTag}>
                                        {getAttributeTranslation(spec.attribute_details?.name || '')}:{' '}
                                        {spec.value}
                                      </span>
                                    ))}
                                  </div>
                                ) : (
                                  <span className={styles.tableDash}>—</span>
                                )}
                              </td>
                            )}
                          </tr>
                        ))
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </Card>

        {/* Team Members — staff and admin only */}
        {isStaffOrAbove() && id && <ProviderUsersSection providerId={id} />}
      </div>
    </div>
  );
}
